import { Component, Input, OnInit, inject } from "@angular/core";
import { BaseDataTablePageDirective } from "@app/view/shared/base-pages/base-data-table-page/base-data-table-page.directive";
import { SystemSearchMetaRestService } from "@app/services/hal/system-search-meta-rest.service";
import { Store } from "@ngrx/store";
import { MatDialog } from "@angular/material/dialog";
import { UntypedFormBuilder } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { ServiceErrorHandlerService } from "@app/view/shared/service-error-handler.service";
import { NgxUiLoaderService } from "ngx-ui-loader";
import { WebhookDefinitionStore } from "@app/services/store/webhook-definition-store";
import { WebhookDefinition } from "@app/models/rest/webhook-definition";
import { WebhookAuthDialogComponent } from "@app/view/setup/system/webhook-auth-dialog/webhook-auth-dialog.component";
import { WebhookDefinitionRestService } from "@app/services/hal/webhook-definition-rest.service";
import { MatToolbar } from "@angular/material/toolbar";
import { MatButton } from "@angular/material/button";
import { GridComponent } from "../../../shared/base-pages/base-data-table-page/grid.component";
import { MatPaginator } from "@angular/material/paginator";
import { TranslatePipe } from "@ngx-translate/core";

@Component({
  selector: "app-webhook-admin-list",
  templateUrl: "./webhook-admin-list.component.html",
  styleUrls: ["./webhook-admin-list.component.css"],
  imports: [MatToolbar, MatButton, GridComponent, MatPaginator, TranslatePipe]
})
export class WebhookAdminListComponent
  extends BaseDataTablePageDirective<WebhookDefinition>
  implements OnInit
{
  webhookStore: WebhookDefinitionStore;
  webhookDefinitionRestService = inject(WebhookDefinitionRestService);
  protected store: Store<any>;
  protected dialog: MatDialog;
  protected formBuilder: UntypedFormBuilder;
  protected router = inject(Router);
  serverErrorHandler: ServiceErrorHandlerService;
  protected ngxUiLoaderService: NgxUiLoaderService;
  private activeRoute = inject(ActivatedRoute);

  @Input()
  public columns: object[] = [
    {
      field: "name",
      text: "Name",
      flex: 1,
      editor: true
    },
    {
      field: "url",
      text: "URL",
      flex: 3,
      editor: true
    },
    {
      field: "retryInterval",
      text: "Retry Delay",
      flex: 1,
      type: "number",
      editor: true
    },
    {
      field: "retryMaximum",
      text: "Retry Attempts",
      flex: 1,
      type: "number",
      editor: true
    },
    {
      field: "enabled",
      text: "Enabled",
      flex: 1,
      type: "check",
      editor: true
    },
    {
      field: "retainLog",
      text: "Retain Log",
      flex: 1,
      type: "check",
      editor: true
    },
    {
      field: "visitCreation",
      text: "Visit Create",
      flex: 1,
      type: "check",
      editor: true
    },
    {
      field: "visitUpdate",
      text: "Visit Update",
      flex: 1,
      type: "check",
      editor: true
    },
    {
      field: "visitStatusChange",
      text: "Visit Status",
      flex: 1,
      type: "check",
      editor: true
    },
    {
      field: "visitBooking",
      text: "Visit Booked",
      flex: 1,
      type: "check",
      editor: true
    },
    {
      field: "visitCancellation",
      text: "Visit Cancelled",
      flex: 1,
      type: "check",
      editor: true
    },
    {
      field: "visitCompletion",
      text: "Visit Completed",
      flex: 1,
      type: "check",
      editor: true
    },
    {
      field: "jobCreation",
      text: "Job Create",
      flex: 1,
      type: "check",
      editor: true
    },
    {
      field: "jobUpdate",
      text: "Job Update",
      flex: 1,
      type: "check",
      editor: true
    },
    {
      field: "ticketDespatched",
      text: "Despatch",
      flex: 1,
      type: "check",
      editor: true
    },
    {
      field: "autoDespatchOnly",
      text: "Auto Only",
      flex: 1,
      type: "check",
      editor: true
    },
    {
      type: "action",
      text: "Auth",
      width: 50,
      editor: false,
      actions: [
        {
          cls: "fa fa-pencil",
          tooltip: "Edit",
          onClick: ({ record }) => {
            if (isNaN(record.id)) {
              console.log("unsaved");
              this.serverErrorHandler.handleClientError(
                "Save changes before editing this record"
              );
              return;
            }
            this.openAuthDialog(record);
          }
        }
      ]
    },
    {
      type: "action",
      text: "History",
      width: 60,
      editor: false,
      actions: [
        {
          cls: "fa fa-clock",
          tooltip: "Edit",
          onClick: ({ record }) => {
            if (isNaN(record.id)) {
              console.log("unsaved");
              this.serverErrorHandler.handleClientError(
                "Unsaved webhooks do not have a call history."
              );
              return;
            }
            this.router
              .navigate([record.id, "log"], { relativeTo: this.activeRoute })
              .then();
          }
        }
      ]
    }
  ];

  constructor() {
    const systemSearchMetaRestService = inject(SystemSearchMetaRestService);
    const webhookStore = inject(WebhookDefinitionStore);
    const store = inject<Store<any>>(Store);
    const dialog = inject(MatDialog);
    const formBuilder = inject(UntypedFormBuilder);
    const serverErrorHandler = inject(ServiceErrorHandlerService);
    const ngxUiLoaderService = inject(NgxUiLoaderService);

    super(
      systemSearchMetaRestService,
      webhookStore,
      store,
      dialog,
      "WebhookDefinition",
      formBuilder,
      serverErrorHandler,
      ngxUiLoaderService
    );

    this.webhookStore = webhookStore;
    this.store = store;
    this.dialog = dialog;
    this.formBuilder = formBuilder;
    this.serverErrorHandler = serverErrorHandler;
    this.ngxUiLoaderService = ngxUiLoaderService;
  }

  public get pristine() {
    return (
      !this.gridStore.changes ||
      (this.gridStore.changes.added.length === 0 &&
        this.gridStore.changes.removed.length === 0 &&
        this.gridStore.changes.modified.length === 0)
    );
  }

  ngOnInit() {
    super.ngOnInit();
    this.gridStore.autoCommit = false;
    this.gridStore.load({});
  }

  addRow() {
    this.gridStore.add([
      {
        name: "Webhook",
        url: "",
        authType: "Basic",
        auth: "",
        enabled: false,
        retryInterval: 5000,
        retryMaximum: 10,

        retainLog: true,

        visitCreation: true,
        visitBooking: true,
        visitCancellation: true,
        visitCompletion: true,
        visitUpdate: true,
        visitStatusChange: true,

        jobCreation: true,
        jobStatusChange: true,
        jobUpdate: true,
        ticketDespatched: true,
        autoDespatchOnly: true
      }
    ]);
  }

  openAuthDialog(record: WebhookDefinition) {
    const dialogRef = this.dialog.open(WebhookAuthDialogComponent, {
      width: "400px",
      data: {}
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        const { username, password } = result;
        const credentials = `${username}:${password}`;
        const encodedAuth = btoa(credentials);

        // Update the record using the service
        this.webhookDefinitionRestService
          .patchResourceById(record.id, {
            body: {
              auth: encodedAuth,
              authType: "Basic"
            }
          })
          .subscribe(
            () => {
              this.serverErrorHandler.handleClientError(
                "Authentication credentials updated successfully."
              );
            },
            (error) => {
              this.serverErrorHandler.handleError(error);
            }
          );
      }
    });
  }
}
